import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AnswerInput, SessionSummary } from '@tm/shared';
import { api, type StartedSession } from '../lib/api.js';
import { haptic } from '../lib/telegram.js';

/** Пауза после верного ответа — ровно чтобы заметить зелёный, но не заскучать. */
const DELAY_RIGHT = 380;
/** После ошибки держим правильный ответ на экране дольше: это момент обучения. */
const DELAY_WRONG = 1200;

type Verdict = { ok: boolean; expected: number } | null;

export function Game({
  session,
  onDone,
  onExit,
}: {
  session: StartedSession;
  onDone: (s: SessionSummary) => void;
  onExit: () => void;
}) {
  const [i, setI] = useState(0);
  const [typed, setTyped] = useState('');
  const [verdict, setVerdict] = useState<Verdict>(null);
  const [combo, setCombo] = useState(0);
  const [timeLeft, setTimeLeft] = useState(session.timeLimitMs);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const answers = useRef<AnswerInput[]>([]);
  const shownAt = useRef<number>(performance.now());
  const locked = useRef(false);
  const finished = useRef(false);

  const task = session.tasks[i];
  const limited = session.timeLimitMs > 0;
  const total = limited ? answers.current.length + 1 : session.tasks.length;

  const finish = useCallback(async () => {
    if (finished.current) return;
    finished.current = true;
    setSending(true);
    try {
      const summary = await api.finishSession(session.sessionId, answers.current);
      onDone(summary);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить результат');
      setSending(false);
    }
  }, [session.sessionId, onDone]);

  // Спринт: минута на всё, время идёт непрерывно.
  useEffect(() => {
    if (!limited) return;
    const started = Date.now();
    const id = setInterval(() => {
      const left = session.timeLimitMs - (Date.now() - started);
      setTimeLeft(Math.max(0, left));
      if (left <= 0) {
        clearInterval(id);
        void finish();
      }
    }, 100);
    return () => clearInterval(id);
  }, [limited, session.timeLimitMs, finish]);

  useEffect(() => {
    shownAt.current = performance.now();
    locked.current = false;
  }, [i]);

  const submit = (value: number) => {
    if (locked.current || !task) return;
    locked.current = true;

    const ms = Math.round(performance.now() - shownAt.current);
    const ok = value === task.answer;
    answers.current.push({ index: task.index, value, ms });

    setVerdict({ ok, expected: task.answer });
    setCombo((c) => (ok ? c + 1 : 0));
    if (ok) haptic.right();
    else haptic.wrong();

    setTimeout(() => {
      setVerdict(null);
      setTyped('');
      if (i + 1 >= session.tasks.length) void finish();
      else setI(i + 1);
    }, ok ? DELAY_RIGHT : DELAY_WRONG);
  };

  const press = (d: string) => {
    if (locked.current) return;
    haptic.tap();
    if (d === '⌫') setTyped((t) => t.slice(0, -1));
    else if (d === '=') {
      if (typed.length > 0) submit(Number(typed));
    } else if (typed.length < 3) setTyped((t) => t + d);
  };

  const progress = useMemo(
    () => (limited ? 1 - timeLeft / session.timeLimitMs : i / session.tasks.length),
    [limited, timeLeft, session.timeLimitMs, i, session.tasks.length],
  );

  if (error) {
    return (
      <div className="stack">
        <div className="card center">
          <p>{error}</p>
          <button className="btn" onClick={onExit}>Вернуться</button>
        </div>
      </div>
    );
  }

  if (sending || !task) {
    return <div className="empty">Считаю результат…</div>;
  }

  const sign = task.op === 'mul' ? '×' : '÷';

  return (
    <div className="game">
      <div className="game-top">
        <button className="pill" onClick={onExit}>✕</button>
        <div className="progress"><i style={{ width: `${Math.min(100, progress * 100)}%` }} /></div>
        {limited ? (
          <span className={timeLeft < 10_000 ? 'timer low' : 'timer'}>
            {Math.ceil(timeLeft / 1000)} с
          </span>
        ) : (
          <span className="timer dim">{Math.min(i + 1, total)}/{total}</span>
        )}
        <span className={combo >= 3 ? 'combo hot' : 'combo'}>
          {combo >= 3 ? `🔥${combo}` : ''}
        </span>
      </div>

      <div className={verdict && !verdict.ok ? 'question shake' : 'question'} key={i}>
        <div>
          {task.left} <span className="eq">{sign}</span> {task.right} <span className="eq">=</span>
          {task.choices === null && <span className="typed">{typed}</span>}
        </div>
      </div>

      <div className={verdict ? (verdict.ok ? 'verdict ok' : 'verdict no') : 'verdict'}>
        {verdict && (verdict.ok ? 'Верно!' : `Правильно: ${verdict.expected}`)}
      </div>

      {task.choices ? (
        <div className="choices">
          {task.choices.map((c) => {
            let cls = 'choice';
            if (verdict) {
              const chosen = answers.current[answers.current.length - 1]?.value;
              if (c === chosen) cls += verdict.ok ? ' right' : ' wrong';
              // При ошибке подсвечиваем верный вариант — ребёнок должен его увидеть.
              else if (!verdict.ok && c === verdict.expected) cls += ' hint';
            }
            return (
              <button key={c} className={cls} onClick={() => submit(c)}>
                {c}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="numpad">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '⌫', '0', '='].map((d) => (
            <button
              key={d}
              className={d === '=' ? 'act' : undefined}
              onClick={() => press(d)}
            >
              {d}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
